<template>
    <button :is="tag"
            :class="[btnClass, {loading}]"
            :disabled="loading || disabled"
            :to="to"
            @click="e => $emit('click', e)"
    >
        <FontAwesomeIcon v-if="icon" class="button-icon" :icon="icon" />
        <slot />
        <template v-if="$slots.loading">
            <slot name="loading" />
        </template>
    </button>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome/index';

export default {
    components: { FontAwesomeIcon },
    props: {
        tag: {
            type: String,
            required: false,
            default: 'button',
            validator: function (value) {
                return ['button', 'a', 'NuxtLink'].indexOf(value) !== -1;
            },
        },
        variant: {
            default: 'primary',
            required: false,
            type: [String],
        },
        loading: {
            default: false,
            required: false,
            type: [Boolean],
        },
        disabled: {
            default: false,
            required: false,
            type: [Boolean],
        },
        to: {
            required: false,
            type: String,
            default: undefined,
        },
        icon: {
            type: [Object, null],
            required: false,
            default: null,
        },
    },
    computed: {
        btnClass: function () {
            if (this.variant === 'confetti') {
                return 'btn btn-primary btn-confetti px-5';
            }
            return `btn btn-${this.variant}`;
        },
    },
};
</script>

<style lang="scss" scoped>
    @import '~@/assets/css/_base';

    $spinner-width: 1.25em;
    $spinner-height: x;
    $spinner-border-width: 3px;

    @keyframes spinner {
        to {
            transform: rotate(360deg);
        }
    }

    .btn.loading {
        color: transparent !important;
        position: relative;

        &:after {
            display: block;
            content: '';
            width: 1.25em;
            height: 1.25em;
            vertical-align: text-bottom;
            border: .1875em solid rgba(white, .30);
            border-right-color: white;
            border-radius: 50%;
            animation: spinner 0.75s linear infinite;
            position: absolute;
            top: calc(50% - .625em);
            left: calc(50% - .625em);
        }

        &.btn-link:after, &.btn-light:after {
            border-color: rgba($black, .15);
            border-right-color: $blue;
        }
    }

    .btn-secondary {
        color: $blue;
    }

    .btn-light {
        background: #F6F6F9;
        color: $text-muted;
        border: none;

        &:hover {
            background: #e2e0e0;
        }

        &.loading {
            &:hover {
                background: transparent;
            }
        }
    }

    .btn-dark-light {
      background: #F6F6F9;
      color: $dark-blue;
      border-radius: 8px;
      border: 1px solid $blue-100;
      padding: 0.25rem 0.5rem;

      &:hover {
        background: #e2e0e0;
      }

      &:disabled {
        border: 1px solid $blue-100;
        opacity: 0.5;
        background: #FFF;
      }
    }

    .btn-warning {
        background: $dark-orange;
    }

    .btn-info {
        background: $cyan;
        border-color: $cyan;
        &:hover {
            background: darken($cyan, 5%);
            border-color: darken($cyan, 5%);
        }
    }

    .btn-transparent {
      background: transparent;
      border-color: $gray-200;

      svg {
        color: $muted;
      }

      &:hover {
        background: darken($gray-100, 1%);
      }
      &:disabled {
        cursor: not-allowed;
        background: $gray-200;
      }
    }

    .btn-gray {
      background: $gray-100;
    }

    .btn-confetti {
        position: relative;
        overflow: hidden;

        &:before, &:after {
            display: block;
            position: absolute;
            width: 4.5rem;
            height: 4.5rem;
            content: '';
            background-image: url("~@/assets/img/confetti.svg");
            background-size: cover;
            background-position: bottom;
        }

        &:before {
            bottom: -1.6rem;
            left: -2.1rem;
            transform: rotate(240deg);
        }

        &:after {
            top: -2.1rem;
            right: -1.6rem;
            transform: rotate(40deg);
        }
    }
    .button-icon {
      min-width: 1.25rem;
      font-size: 0.75rem;
    }
</style>
